import * as React from 'react';
import Column from '@sdk/components/ui/spacing/Column';
import gettext from '@sdk/lang/gettext';
import Picker from '@sdk/components/forms/Picker';
import useAuth from '@sdk/authentication/useAuth';
import { UserCanStartConversationOption } from '@sdk/authentication/types';
import { Feature } from '@sdk/common/features';
import type { Sections } from '@sdk/components/ui/anchorMenu/createSections';

import PremiumGuard from '../premiumGuard/PremiumGuard';
import usePremiumGuard from '../premiumGuard/usePremiumGuard';
import getPremiumTooltipId from '../premiumGuard/getPremiumTooltipId';
import { PremiumModalNames } from '../premiumGuard/constants/modalNames';

import SettingsSection from './components/SettingsSection';
import CollapsibleSettingsSection from './components/CollapsibleSettingsSection';
import SettingsHeader from './components/SettingsHeader';
import SettingsSwitcherRow from './components/SettingsSwitcherRow';
import { CONVERSATION_OPTIONS } from './constants';
import { useUserOrgSettings } from './UserOrgSettingsContext';

type Props = {
  orgHasOnlyOneLocation?: boolean;
  sectionsConfig: Sections;
};

const CompanySettingsScreen: React.FC<React.PropsWithChildren<Props>> = ({
  orgHasOnlyOneLocation,
  sectionsConfig,
}) => {
  const { currentUser, isOrgFeatureEnabled } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();

  const onUsersCanSeeUsersOutsideLocationChange = (value: boolean): void => {
    if (!value) {
      onUserOrgSettingsChange({
        usersCanSeeUsersOutsideLocation: value,
        usersCanSeeSchedulesInOtherLocations: value,
        shiftExchangeOnlyMatchingGroups: true,
      });
      return;
    }

    onUserOrgSettingsChange({ usersCanSeeUsersOutsideLocation: value });
  };

  return (
    <>
      <SettingsHeader
        ref={sectionsConfig.refs.restrictions}
        withSeparator
        title={gettext('Restrictions')}
      />
      {!orgHasOnlyOneLocation && (
        <PremiumGuard
          premiumFeature={Feature.usersCanSeeUsersOutsideLocation}
          premiumGuardModalName={PremiumModalNames.usersCanSeeUsersOutsideLocation}
          hasAccess={isOrgFeatureEnabled(Feature.usersCanSeeUsersOutsideLocation)}
          tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.usersCanSeeUsersOutsideLocation)]}>
          <SettingsSwitcherRow
            premiumGuard={getPremiumGuardConfig(Feature.usersCanSeeUsersOutsideLocation)}
            disabled={!currentUser.isAdmin}
            withSeparator
            title={gettext('Visibility of coworkers across locations')}
            subtitle={gettext(
              'Allow employees to view and communicate with coworkers assigned to different locations.'
            )}
            value={userOrgSettings.usersCanSeeUsersOutsideLocation}
            onChange={(value): void => onUsersCanSeeUsersOutsideLocationChange(value)}
          />
        </PremiumGuard>
      )}

      <SettingsSwitcherRow
        disabled={!currentUser.isAdmin}
        title={gettext('Contact details')}
        subtitle={gettext('Allow employees to see each other’s phone numbers and emails.')}
        onChange={(value): void => onUserOrgSettingsChange({ usersSeePersonalData: value })}
        value={userOrgSettings.usersSeePersonalData}
        withSeparator
      />

      {
        /*-----------------------MESSAGE SETTINGS----------------------------*/
        isOrgFeatureEnabled(Feature.groupMessages) ? (
          <SettingsSection pTop={20} pBottom={20}>
            <SettingsHeader
              ref={sectionsConfig.refs.messages}
              title={gettext('Messages')}
              premiumGuard={getPremiumGuardConfig(Feature.groupMessages)}
            />
            <CollapsibleSettingsSection
              withSeparator
              isActive={!userOrgSettings.messagesFeatureDisabled}
              headerItem={
                <SettingsSwitcherRow
                  disabled={!currentUser.isAdmin}
                  subtitle={gettext('Enable group and private messaging for your organization.')}
                  value={!userOrgSettings.messagesFeatureDisabled}
                  onChange={(value): void =>
                    onUserOrgSettingsChange({ messagesFeatureDisabled: !value })
                  }
                />
              }
              contentItem={
                <>
                  <CollapsibleSettingsSection
                    isActive={
                      userOrgSettings.usersCanStartConversation !==
                      UserCanStartConversationOption.none
                    }
                    headerItem={
                      <SettingsSwitcherRow
                        disabled={!currentUser.isAdmin}
                        title={gettext('Allow employees to start conversations')}
                        subtitle={gettext('Employees will be able to create new conversations.')}
                        value={
                          userOrgSettings.usersCanStartConversation !==
                          UserCanStartConversationOption.none
                        }
                        onChange={(value): void =>
                          onUserOrgSettingsChange({
                            usersCanStartConversation: value
                              ? UserCanStartConversationOption.all
                              : UserCanStartConversationOption.none,
                          })
                        }
                      />
                    }
                    contentItem={
                      <Column pTop={20} pBottom={20}>
                        <Picker
                          disabled={!currentUser.isAdmin}
                          options={CONVERSATION_OPTIONS}
                          value={userOrgSettings.usersCanStartConversation}
                          onChange={(value: UserCanStartConversationOption): void =>
                            onUserOrgSettingsChange({ usersCanStartConversation: value })
                          }
                        />
                      </Column>
                    }
                  />
                </>
              }
            />
          </SettingsSection>
        ) : (
          <SettingsSection pTop={20} pBottom={20}>
            <SettingsHeader ref={sectionsConfig.refs.messages} title={gettext('Messages')} />
            <CollapsibleSettingsSection
              withSeparator
              isActive={!userOrgSettings.messagesFeatureDisabled}
              headerItem={
                <SettingsSwitcherRow
                  disabled={!currentUser.isAdmin}
                  subtitle={gettext('Enable messaging for your organization.')}
                  value={!userOrgSettings.messagesFeatureDisabled}
                  onChange={(value): void =>
                    onUserOrgSettingsChange({ messagesFeatureDisabled: !value })
                  }
                />
              }
              contentItem={
                <>
                  <SettingsSection>
                    <SettingsSwitcherRow
                      disabled={!currentUser.isAdmin}
                      title={gettext('Allow employees to start conversations')}
                      subtitle={gettext('Employees will be able to create new conversations.')}
                      value={
                        userOrgSettings.usersCanStartConversation !==
                        UserCanStartConversationOption.none
                      }
                      onChange={(value): void =>
                        onUserOrgSettingsChange({
                          usersCanStartConversation: value
                            ? // free orgs can only create private conversations
                              UserCanStartConversationOption.privateOnly
                            : UserCanStartConversationOption.none,
                        })
                      }
                    />
                  </SettingsSection>
                </>
              }
            />
          </SettingsSection>
        )
        /*--------------------END MESSAGE SETTINGS---------------------------*/
      }

      <PremiumGuard
        hasAccess={currentUser.org.isPremium}
        premiumGuardModalName={PremiumModalNames.enableNewsfeed}
        premiumFeature={Feature.newsfeed}
        tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.newsfeed)]}>
        <SettingsSection pBottom={20}>
          <SettingsHeader
            ref={sectionsConfig.refs.newsfeed}
            title={gettext('Newsfeed')}
            premiumGuard={getPremiumGuardConfig(Feature.newsfeed)}
          />
          <CollapsibleSettingsSection
            withSeparator
            isActive={!userOrgSettings.newsfeedFeatureDisabled}
            headerItem={
              <SettingsSwitcherRow
                disabled={!currentUser.isAdmin}
                subtitle={gettext('Enable content sharing across your organization.')}
                value={!userOrgSettings.newsfeedFeatureDisabled}
                onChange={(value): void =>
                  onUserOrgSettingsChange({ newsfeedFeatureDisabled: !value })
                }
              />
            }
            contentItem={
              <SettingsSwitcherRow
                title={gettext('Allow employees to create pages')}
                subtitle={gettext('Employees will be able to create newsfeed pages.')}
                value={userOrgSettings.usersCanCreatePages}
                disabled={!currentUser.isAdmin}
                onChange={(value): void => onUserOrgSettingsChange({ usersCanCreatePages: value })}
              />
            }
          />
        </SettingsSection>
      </PremiumGuard>

      <PremiumGuard
        hasAccess={currentUser.org.isPremium}
        premiumGuardModalName={PremiumModalNames.shiftTasks}
        premiumFeature={Feature.shiftTasks}
        tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.shiftTasks)]}>
        <>
          <SettingsHeader
            ref={sectionsConfig.refs.tasks}
            title={gettext('Tasks')}
            premiumGuard={getPremiumGuardConfig(Feature.shiftTasks)}
          />
          <SettingsSwitcherRow
            disabled={!currentUser.isAdmin}
            subtitle={gettext('Enable to assign tasks to shifts and follow their progress.')}
            value={!userOrgSettings.tasksFeatureDisabled}
            onChange={(value): void => onUserOrgSettingsChange({ tasksFeatureDisabled: !value })}
            withMainHeader
          />
        </>
      </PremiumGuard>
    </>
  );
};

export default CompanySettingsScreen;
